// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { injectCss, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xHeaderUpdate from "@steiner/App/Handlers/XNG/Header";
import useElementDimensions from "@steiner/App/Hooks/UseElementDimensions";
import {
    getHeaderSearchInputWidgetFields,
    getMainNavigationWidgetFields,
    getMiniCartWidgetFields,
    getMiniSignInWidgetFields,
} from "@steiner/App/Store/Selectors/WidgetSelectors";
import { layoutContainer, setCSSVariable } from "@steiner/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

type Props = PageProps & HasShellContext & ThemeProps<BaseTheme>;
export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    /**
     * XNG-Changes
     */
    linkListWrapper?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 50%;
            z-index: 1;
            opacity: 0;
            transform: translateY(-100%) translateX(-50%);
            padding: ${props => props.theme.header.padding};
            ${generateBorder}
            ${generateShadow};
        `,
    },
    logoWrapper: {
        css: css`
            align-self: center;
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = compactHeaderStyles;

// XNG-Changes: Move generateBorder and generateShadow functions above
export const createStylesProviderStyles = (theme: BaseTheme) => ({}) as Styles;

/**
 * XNG-Changes
 * Change translateX to be 0 all the time
 */
const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

/**
 * XNG-Changes
 */
const InnerWrapper = styled.div<{ isEdit?: boolean } & InjectableCss>`
    display: grid;
    grid-template-columns: ${props => (props.isEdit ? "200px 200px auto 200px 200px" : "66px 50px auto 36px 60px")};
    padding: ${props => props.isEdit && "30px 10px"};
    width: 100%;
    margin: 0 auto;
    ${({ theme, isEdit }) => {
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : css`
                  ${layoutContainer}
              `;
    }}
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    const transitionElement = React.useRef<HTMLElement | null>(null);

    // <XNG-Changes>
    const [bodyRef, bodySize] = useElementDimensions<HTMLElement>();
    const dispatch = useDispatch();

    const [headerRef, size] = useElementDimensions();

    const {
        headerActionsWidgets,
        headerSearchInputWidgetFields,
        mainNavigationWidgetFields,
        miniSignInWidgetFields,
        miniCartWidgetFields,
    } = useAppSelector(state => {
        const headerActionsWidgets = getWidgetsByPageId(state, getHeader(state).id)
            .filter(widget => widget.zone === "LinkList" && widget.type !== "Header/HeaderLinkList")
            .map(widget => ({
                id: widget.id,
                type: widget.type,
                fields: widget.fields,
            }));

        return {
            headerSearchInputWidgetFields: getHeaderSearchInputWidgetFields(state),
            mainNavigationWidgetFields: getMainNavigationWidgetFields(state),
            miniSignInWidgetFields: getMiniSignInWidgetFields(state),
            miniCartWidgetFields: getMiniCartWidgetFields(state),
            headerActionsWidgets,
        };
    }, shallowEqual);
    // </XNG-Changes>

    /**
     * XNG-Changes
     * Assign bodyRef to document.body
     * By this step, we can calculate maxWidth for viewport
     */
    useEffect(() => {
        if (typeof window !== "undefined") {
            bodyRef.current = document.body;
        }
    }, []);

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            const windowWidth = Math.round(window.innerWidth);

            if (scrollPos > Number(props.theme.header.triggerPoint) && windowWidth > 767) {
                setIsShow(true);
                dispatch(xHeaderUpdate({ key: "isSticky", value: true }) as any);
            } else if ((isShow && scrollPos < Number(props.theme.header.triggerPoint)) || windowWidth < 768) {
                setIsShow(false);
                dispatch(xHeaderUpdate({ key: "isSticky", value: false }) as any);
            }
        }

        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    useEffect(() => {
        setCSSVariable("--compact-header-height", `${size?.height || 47}px`);
        // XNG-Changes
        dispatch(xHeaderUpdate({ key: "stickyHeaderHeight", value: size?.height || 47 }) as any);
    }, [size?.height]);

    return (
        <Transition nodeRef={bodyRef} in={isShow} timeout={0}>
            {state => (
                <StyledWrapper
                    {...styles.page}
                    ref={headerRef}
                    as="header"
                    style={{
                        transition: "all 350ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        maxWidth: bodySize?.width || "100%",
                    }}
                >
                    <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                        <InnerWrapper isEdit={isEdit} {...styles.pageInnerWrapper}>
                            <StyledWrapper {...styles.mainNavigationWrapper}>
                                {createWidgetElement("Header/MainNavigation", {
                                    fields: {
                                        ...mainNavigationWidgetFields,
                                    },
                                    isCompactHeaderFlyoutMenu: true,
                                } as any)}
                            </StyledWrapper>
                            <StyledWrapper {...styles.searchWrapper}>
                                {createWidgetElement("Header/HeaderSearchInput", {
                                    fields: { ...headerSearchInputWidgetFields },
                                })}
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkListWrapper}>
                                {headerActionsWidgets.map(widget => (
                                    <React.Fragment key={widget.id}>
                                        {createWidgetElement(widget.type, {
                                            fields: {
                                                ...widget.fields,
                                                isCompactHeader: true,
                                            },
                                        })}
                                    </React.Fragment>
                                ))}
                                {createWidgetElement("Common/HeaderSignIn", {
                                    fields: { ...miniSignInWidgetFields, isCompactHeader: true },
                                })}
                                {createWidgetElement("Common/CartLink", {
                                    fields: { ...miniCartWidgetFields },
                                    isCompactHeaderCart: true,
                                } as any)}
                            </StyledWrapper>
                        </InnerWrapper>
                    </StylesProvider>
                </StyledWrapper>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: withTheme(withIsInShell(CompactHeader)),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
